import {InteractiveSphere} from "./shapes/Sphere.tsx";
import type {SidebarValues} from "../App.tsx";
import {type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {maxHorizontalAngel, maxVerticalAngel, minDB, type SceneBoundaries, type SourceState} from "../Sources.tsx";
import type {AudioPlayer} from "../player/AudioPlayer";
import StreamPlayer from "../player/StreamPlayer";
import WebApiPlayer from "../player/WebApiPlayer";
import ThreePlayer from "../player/ThreePlayer";
import {useThree} from "@react-three/fiber";
import * as THREE from "three";
import {InteractiveCube} from "./shapes/Cube.tsx";
import {InteractiveTetrahedron} from "./shapes/Tetrahedron.tsx";
import {ShapeController} from "../controller/ShapeController.ts";
import type {TouchingBoundary} from "../Scene.tsx";

export type SourceType = "intern" | "fetch" | "stream";
export type Shape = "sphere" | "cube" | "tetrahedron";

/**
 * Initial setup for the source.
 * @param dB in dB, min is -60, max is 0
 * @param horizontal panning in degree, min is -90, max is 90
 * @param vertical panning in degree, min is -45, max is 45
 */
type InitialData = { dB?: number, horizontal?: number, vertical?: number }

type OrientationData = { rotY?: number, rotX?: number };
type PanningData = { horizontal?: boolean, vertical?: boolean };

type SourceProps = {
    boundaries: SceneBoundaries,
    label?: string,
    url: string,
    sourceType?: SourceType,
    autoload?: boolean,
    loop?: boolean,
    initial?: InitialData,
    shape?: Shape,
    size: number,
    orientation?: OrientationData,
    panning?: PanningData,
    setTouchingBoundary: Dispatch<SetStateAction<TouchingBoundary>>;
    setValues: SidebarValues;
}

// singleton
const audioContext = new (globalThis.AudioContext)();

/**
 * A source of audio.
 * It is represented by an 3D object in the 3D scene.
 *
 * @param label optional label for the source
 * @param sourceType type of the source, "intern" (default), "fetch" or "stream"
 * @param autoload whether to load the source automatically, default is true
 * @param loop set the audio to loop infinitely
 * @param url the source of the audio
 * @param shape shape of the object, "sphere" (default), "cube" or "tetrahedron"
 * @param orientation rotation in degrees { rotY?, rotX? }, each -90 to +90, default is 0
 * @param panning panning options { horizontal?, vertical? }, defaults are both true
 * @param initial initial data for the source
 * @param boundaries values describing the scene boundaries
 * @param setTouchingBoundary callback to notify the boundary is touched
 * @param setValues callback to set values in the sidebar
 */
export default function Source({label, sourceType = "intern", autoload = true, loop = false, url,
                                   shape = "sphere", size, orientation, panning = {}, initial, boundaries, setTouchingBoundary,
                                   setValues }: SourceProps) {

    const { horizontal: panHorizontal = true, vertical: panVertical = true } = panning;

    const [state, setState] = useState<SourceState>("none");

    const shapeComponents : Record<Shape, any> = {
        cube: InteractiveCube,
        tetrahedron: InteractiveTetrahedron,
        sphere: InteractiveSphere
    } as const;

    const [isHovered, setIsHovered] = useState(false)

    const initialPosition = useMemo(() => {
        //console.trace("initial position");
        const clamp = (min: number, max: number, defaultValue: number, value?: number): number => {
            if (min > max) console.error(`${min} must be <= ${max}`);
            if (value === undefined) value = defaultValue;
            if (value > max) console.error(`${value} must be <= ${max}`);
            if (value < min) console.error(`${value} must be >= ${min}`);
            return Math.min(Math.max(value, min), max);
        }
        const clamped = {
            dB: clamp(minDB, 0, -6, initial?.dB),
            horizontal: clamp(-maxHorizontalAngel, maxHorizontalAngel, 0, initial?.horizontal),
            vertical: clamp(-maxVerticalAngel, maxVerticalAngel, 0, initial?.vertical)
        }
        return {
            x: clamped.horizontal * boundaries.maxX / maxHorizontalAngel,
            y: clamped.vertical * boundaries.maxY / maxVerticalAngel,
            z: clamped.dB * boundaries.minZ / minDB
        }
    }, [initial?.dB, initial?.horizontal, initial?.vertical,
        boundaries.maxX, boundaries.maxY, boundaries.minZ]);

    const [position, setPosition] = useState<{ x: number, y: number, z: number }>(initialPosition);
    const calcNormed = () => {
        return {
            x: position.x / boundaries.maxX,
            y: position.y / boundaries.maxY,
            z: 1 - position.z / boundaries.minZ // min 0, max 1
        };
    }
    const [normed, setNormed] = useState<{ x: number, y: number, z: number }>(calcNormed());

    // Adapt position when boundaries change
    useEffect(() => {
        //console.log("boundaries changed to " + JSON.stringify(boundaries));
        const adapted = {
            x: normed.x * boundaries.maxX,
            y: normed.y * boundaries.maxY,
            z: (1 - normed.z) * boundaries.minZ
        }
        setPosition(adapted);
    }, [boundaries.maxX, boundaries.maxY, boundaries.minZ]);

    const {scene, camera} = useThree();
    const player: AudioPlayer = useMemo(() => {
        switch (sourceType) {
            case "intern":
                return new ThreePlayer(scene, camera, url, setState);
            case "fetch":
                return new WebApiPlayer(audioContext, url, setState);
            case "stream":
                return new StreamPlayer(audioContext, url, setState);
        }
    }, [sourceType, scene, camera, url]);

    const load = useCallback(() => {
        if (state === "none") {
            player.load((ready: boolean) => {
                console.log(label + " loaded, now " + (ready ? "ready" : "error"));
                if (!ready) return;
                loaded();
            })
        }
    }, [player, state, url]);

    function loaded() {
        if (loop && player.loop) {
            player.loop();
            console.log(label + " loops");
        }
    }

    const doClick = useCallback(() => {
        if (state === "ready" || state === "running") {
            player.toggle();
        }

        if (state === "none" || state === "error") {
            player.load((ready: boolean) => {
                console.log(label + " loaded on click, now " + (ready ? "ready" : "error"));
                if (!ready) return;
                loaded();
            })
        }
    }, [state, player, url]);

    function decibel(z: number) {
        return z ? (1 - z) * minDB : minDB;
    }

    const positionPlayback = useCallback(() => {
        //console.trace("source - position playback");

        setNormed(calcNormed());

        const db = decibel(normed.z);
        player.volume(db);

        if (normed.x && normed.y) player.pan(normed.x, normed.y);

        setValues((prev) => {
            const { x, y, ...rest } = prev;
            return {
                ...rest, // keep monitoring values
                ...(panHorizontal && {"x": normed.x}),
                ...(panVertical && {"y": normed.y}),
                "z": normed.z,
                "volume": db,
                "touching": touchingObject.toString()
            };
        });

    }, [player, position.x, position.y, position.z, setValues]);

    useEffect(() => {
        //console.trace("source use effect - state");
        if (state === "none" && autoload) load();
        positionPlayback();

        setValues((prev) => ({
            ...prev,
            "source": label ? label : url,
            "state": state,
        }));

    }, [url, state, isHovered, setValues, positionPlayback, autoload, load, label]);

    const meshRef = useRef<THREE.Mesh | null>(null);
    const [touchingObject, setTouchingObject] = useState(false);
    const shapeController = useMemo(() => new ShapeController({
        bounds: boundaries,
        panning: { horizontal: panHorizontal, vertical: panVertical },
        setIsHovered,
        setPosition,
        doClicked: doClick,
        setValues,
        setTouchingBoundary,
        setTouchingObject
    }, meshRef), [boundaries, setIsHovered, setPosition, doClick, setValues, setTouchingObject]);

    const ShapeComponent = shapeComponents[shape];
    return (<>
         <ShapeComponent controller={shapeController}
                         diameter={size} minZ={boundaries.minZ}
                           position={position}
                           state={state}
                           orientation={orientation}
                           touchingObject={touchingObject}
        />;
    </>)
}
